
import Vue from 'vue';
import { orgStorage } from '@/utils/org-storage';


const handleError = function (xhr_error) {
    const error = {
        success: false,
        code: xhr_error.response?.status || xhr_error.request?.status || xhr_error.code
    };

    if (error.code == null) {
        console.error(xhr_error);
    }

    if (!xhr_error.request || error.code == 500) {
        if (Vue.prototype.$translate) {
            error.details = [Vue.prototype.$translate('errors.anUnexpectedErrorHappened')];
        } else {
            error.details = ['An unexpected error happened'];
        }
    } else if (xhr_error.response) {
        error.details = xhr_error.response.data;
    }
    return error;
};

/** Map 403 to 404 for resource pages (do not leak existence of forbidden resources). */
const handleResourceError = function (xhr_error) {
    const error = handleError(xhr_error);
    if (error.code === 403) {
        return { ...error, code: 404 };
    }
    return error;
};

const isResourceNotFoundError = function (error) {
    return Boolean(error && (error.code === 404 || error.code === 403));
};


function isColorDark(color) {
    function luma(color) // color can be a hx string or an array of RGB values 0-255
    {
        var rgb = (typeof color === 'string') ? hexToRGBArray(color) : color;
        return (0.2126 * rgb[0]) + (0.7152 * rgb[1]) + (0.0722 * rgb[2]); // SMPTE C, Rec. 709 weightings
    }
    function hexToRGBArray(color) {
        if (color.length === 3)
            color = color.charAt(0) + color.charAt(0) + color.charAt(1) + color.charAt(1) + color.charAt(2) + color.charAt(2);
        else if (color.length !== 6)
            throw ('Invalid hex color: ' + color);
        var rgb = [];
        for (var i = 0; i <= 2; i++)
            rgb[i] = parseInt(color.substr(i * 2, 2), 16);
        return rgb;
    }
    if (color.startsWith('#')) {
        color = color.substr(1, 6);
    }
    return (luma(color) >= 165) ? false : true
}

// Helper function to generate storage key based on module and section
// Omit `section` (or pass null) for filters shared across all tabs of a list.
const getStorageKey = (moduleName, section) => {
    return `${moduleName}-filters-${section || 'default'}`;
};

/** Pagination / sort keys — defaults from the section, overridden by user prefs. */
const SECTION_FILTER_PAGINATION_KEYS = ['limit', 'offset', 'ordering'];

/**
 * Keys applied by a list tab/section that must win over user filters at query time.
 * Pagination keys are excluded — they are user preferences with section defaults.
 */
const getSectionConstraintKeys = (sectionFilters) => {
    if (!sectionFilters || typeof sectionFilters !== 'object') {
        return [];
    }
    return Object.keys(sectionFilters).filter(
        (key) => !SECTION_FILTER_PAGINATION_KEYS.includes(key),
    );
};

const omitFilterKeys = (filters, keys) => {
    const next = { ...(filters || {}) };
    for (const key of keys) {
        delete next[key];
    }
    return next;
};

/**
 * Merge user list filters with tab/section constraints.
 * User wins on search / buyer / dates / limit / ordering; section wins on constraint keys
 * (e.g. status, paid, invoices_*).
 *
 * Pattern for tabbed lists: persist user filters on a shared storage key (no section),
 * and re-apply `sectionFilters` on every section change and before each API call.
 * Table stays agnostic — it only receives the effective `filters` object.
 */
const mergeUserAndSectionFilters = (userFilters, sectionFilters) => {
    const section = sectionFilters || {};
    const constraintKeys = getSectionConstraintKeys(section);
    const constraints = {};
    for (const key of constraintKeys) {
        constraints[key] = section[key];
    }
    return {
        ...section,
        ...(userFilters || {}),
        ...constraints,
    };
};

/**
 * Derive persistable user filters from the effective filters bound to Table.
 * Section constraint values (e.g. status forced by the tab) are stripped and previous
 * user values for those keys are preserved so switching back to "all" restores them.
 */
const extractUserFiltersFromEffective = (
    effectiveFilters,
    sectionFilters,
    previousUserFilters = {},
) => {
    const constraintKeys = getSectionConstraintKeys(sectionFilters);
    const next = omitFilterKeys(effectiveFilters, [...constraintKeys, 'offset']);
    for (const key of constraintKeys) {
        if (previousUserFilters[key] !== undefined) {
            next[key] = previousUserFilters[key];
        }
    }
    return next;
};

// Helper function to generate column layout storage key for a table scope
const getColumnStorageKey = (scope) => {
    if (!scope) {
        return null;
    }
    return `${String(scope).replace(/\./g, '-')}-columns`;
};

// Helper function to generate active table view storage key for a scope
const getActiveViewStorageKey = (scope) => {
    if (!scope) {
        return null;
    }
    return `${String(scope).replace(/\./g, '-')}-active-view`;
};

// Helper function to generate filters panel visibility storage key for a table scope
const getFiltersPanelStorageKey = (scope) => {
    if (!scope) {
        return null;
    }
    return `${String(scope).replace(/\./g, '-')}-filters-panel`;
};

// Helper function to load filters from localStorage with defaults
// Uses orgStorage to automatically prefix keys with the current organization ID
const loadFiltersFromStorage = (storageKey, defaults, fieldsToExclude) => {
    if (!storageKey) {
        return defaults;
    }
    try {
        const saved = orgStorage.getItem(storageKey);
        if (saved) {
            const parsed = JSON.parse(saved);
            // Merge saved values with defaults, preferring saved values
            for (const fieldToExclude of (fieldsToExclude || [])) {
                if (parsed[fieldToExclude]) {
                    delete parsed[fieldToExclude];
                }
            }
            delete parsed.assigned_to;
            return { ...defaults, ...parsed };
        }
    } catch (e) {
        console.warn('Failed to load filters from orgStorage:', e);
    }
    return defaults;
};

const DEFAULT_STOCK_TAB_PAGE_SIZE = 20;

/** Rebuild tab filters from the last successful stock table API request signature. */
const filtersFromStockTableCache = (cachedRequest) => {
    const defaults = {
        limit: DEFAULT_STOCK_TAB_PAGE_SIZE,
        offset: 0,
        search: null,
        stock: null,
        stock_owner: null,
    };
    if (!cachedRequest) {
        return defaults;
    }
    try {
        const parsed = JSON.parse(cachedRequest);
        return {
            limit: parsed.limit ?? defaults.limit,
            offset: parsed.offset ?? defaults.offset,
            search: parsed.search || null,
            stock: parsed.stock ?? null,
            stock_owner: parsed.stock_owner || null,
        };
    } catch {
        return defaults;
    }
};

// Helper function to save specific filter fields to localStorage
// Uses orgStorage to automatically prefix keys with the current organization ID
const saveFiltersToStorage = (storageKey, filters, fieldsToSave) => {
    if (!storageKey) {
        return;
    }
    try {
        let toSave = {};
        
        if (fieldsToSave) {
            // Save only specified fields
            fieldsToSave.forEach(field => {
                if (filters[field] !== undefined && filters[field] !== null) {
                    toSave[field] = filters[field];
                }
            });
        } else {
            // Save all fields if fieldsToSave is not defined
            toSave = { ...filters };
        }
        orgStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch (e) {
        console.warn('Failed to save filters to orgStorage:', e);
    }
};




export default {
    handleError,
    handleResourceError,
    isResourceNotFoundError,
    isColorDark,
    getStorageKey,
    getColumnStorageKey,
    getActiveViewStorageKey,
    getFiltersPanelStorageKey,
    loadFiltersFromStorage,
    saveFiltersToStorage,
    filtersFromStockTableCache,
    getSectionConstraintKeys,
    omitFilterKeys,
    mergeUserAndSectionFilters,
    extractUserFiltersFromEffective,
};
